import { useState, useEffect, useRef } from "react";
import { GoogleRoutingProvider } from "../services/routing/providers/google.provider";
import type { Coordinates, RouteResult } from "../services/routing/routing.types";
import type { IJourney } from "../types/events";

interface JourneyRoutes {
  /** Origin → current position (completed portion) */
  completed: Coordinates[];
  /** Current position → destination (remaining portion) */
  remaining: Coordinates[];
}

const routingProvider = new GoogleRoutingProvider();

/**
 * Fetches Google Directions routes for active journeys.
 * Caches routes by journey ID + current position to avoid redundant API calls.
 * Re-fetches when the user's current position changes significantly.
 */
export function useJourneyRoutes(journeys: IJourney[]) {
  const [routes, setRoutes] = useState<Record<number, JourneyRoutes>>({});
  const cacheKeyRef = useRef<Record<number, string>>({});

  useEffect(() => {
    if (journeys.length === 0) return;

    let cancelled = false;

    const fetchRoutes = async () => {
      const newRoutes: Record<number, JourneyRoutes> = { ...routes };
      let hasChanges = false;

      await Promise.all(
        journeys.map(async (journey) => {
          // Build a cache key based on journey ID + rounded current position
          // Only re-fetch if position moved by ~100m
          const roundedLat = Math.round(journey.currentLatitude * 1000) / 1000;
          const roundedLng = Math.round(journey.currentLongitude * 1000) / 1000;
          const cacheKey = `${journey.id}-${roundedLat}-${roundedLng}`;

          if (cacheKeyRef.current[journey.id] === cacheKey) return;

          try {
            const [completedRoute, remainingRoute] = await Promise.all([
              routingProvider.getRoute(
                [journey.originLatitude, journey.originLongitude],
                [journey.currentLatitude, journey.currentLongitude],
                { mode: "driving" }
              ),
              routingProvider.getRoute(
                [journey.currentLatitude, journey.currentLongitude],
                [journey.destinationLatitude, journey.destinationLongitude],
                { mode: "driving" }
              ),
            ]);

            if (!cancelled) {
              newRoutes[journey.id] = {
                completed: completedRoute.coordinates,
                remaining: remainingRoute.coordinates,
              };
              cacheKeyRef.current[journey.id] = cacheKey;
              hasChanges = true;
            }
          } catch (error) {
            console.warn(`[useJourneyRoutes] Failed to fetch route for journey ${journey.id}:`, error);
            // Fallback: straight lines
            if (!cancelled) {
              newRoutes[journey.id] = {
                completed: [
                  [journey.originLatitude, journey.originLongitude],
                  [journey.currentLatitude, journey.currentLongitude],
                ],
                remaining: [
                  [journey.currentLatitude, journey.currentLongitude],
                  [journey.destinationLatitude, journey.destinationLongitude],
                ],
              };
              cacheKeyRef.current[journey.id] = cacheKey;
              hasChanges = true;
            }
          }
        })
      );

      if (!cancelled && hasChanges) {
        setRoutes({ ...newRoutes });
      }
    };

    fetchRoutes();

    return () => {
      cancelled = true;
    };
  // Only re-run when journey list or positions change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(journeys.map((j) => [j.id, Math.round(j.currentLatitude * 1000), Math.round(j.currentLongitude * 1000)]))]);

  return routes;
}
